import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';
import { observeChartSize } from '../utils/chartResize';
import {
  applyVisibleRange,
  DEFAULT_ZOOM,
  ZOOM_RANGES,
} from '../utils/etpListingHelpers';
import './InvestingPage.css';
import './EtpPricesPage.css';

function fmtFxRate(value, quoteCcy) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (quoteCcy === 'JPY') {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return n.toFixed(4);
}

function fxBarsToLine(bars) {
  if (!bars?.length) return [];
  return bars
    .filter((b) => b?.time && b.close != null && !Number.isNaN(Number(b.close)))
    .map((b) => ({ time: b.time, value: Number(b.close) }));
}

function FxPage() {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const lineSeriesRef = useRef(null);
  const parityLineRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [bars, setBars] = useState([]);
  const [zoomRange, setZoomRange] = useState(DEFAULT_ZOOM);
  const zoomRangeRef = useRef(DEFAULT_ZOOM);

  useEffect(() => {
    zoomRangeRef.current = zoomRange;
  }, [zoomRange]);

  useEffect(() => {
    let cancelled = false;

    async function loadCatalog() {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch('/api/investing/fx');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = await response.json();
        if (cancelled) return;
        const crosses = data.crosses || [];
        setRows(crosses);
        setMeta({
          fetched_at: data.fetched_at,
          fx_fetched_today: data.fx_fetched_today,
          count: data.count,
        });
        if (crosses.length === 0) {
          throw new Error(
            'No FX crosses in DB — run Flyway V017 and: python -m investing.backend --fx-only --force-fx',
          );
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load FX catalog');
          setRows([]);
          setMeta(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => a.cross_id.localeCompare(b.cross_id)),
    [rows],
  );

  useEffect(() => {
    if (sortedRows.length === 0) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow((prev) => {
      if (prev && sortedRows.some((r) => r.cross_id === prev.cross_id)) {
        return prev;
      }
      return sortedRows[0];
    });
  }, [sortedRows]);

  useEffect(() => {
    if (!selectedRow || !chartContainerRef.current) return undefined;

    parityLineRef.current = null;

    const chart = createChart(chartContainerRef.current, {
      layout: { background: { color: '#FFFBF3' }, textColor: '#16244A' },
      grid: { vertLines: { color: '#EDE3CF' }, horzLines: { color: '#EDE3CF' } },
      crosshair: { mode: 1 },
      rightPriceScale: { borderColor: '#E3D9C6' },
      timeScale: { borderColor: '#E3D9C6', timeVisible: true, secondsVisible: false },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false,
      },
      handleScale: {
        axisPressedMouseMove: true,
        axisDoubleClickReset: true,
        mouseWheel: true,
        pinch: true,
      },
      width: chartContainerRef.current.clientWidth,
      height: chartContainerRef.current.clientHeight,
    });

    const lineSeries = chart.addSeries(LineSeries, {
      color: '#1F4E8C',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    chartRef.current = chart;
    lineSeriesRef.current = lineSeries;

    const disconnectResize = observeChartSize(chartContainerRef.current, (width, height) => {
      chart.applyOptions({ width, height });
    });

    return () => {
      disconnectResize();
      chart.remove();
      chartRef.current = null;
      lineSeriesRef.current = null;
      parityLineRef.current = null;
    };
  }, [selectedRow?.cross_id]);

  useEffect(() => {
    if (!selectedRow) {
      setBars([]);
      setChartError(null);
      lineSeriesRef.current?.setData([]);
      return;
    }

    let cancelled = false;

    async function loadChartData() {
      setChartLoading(true);
      setChartError(null);
      try {
        const crossId = encodeURIComponent(selectedRow.cross_id);
        const priceResponse = await fetch(`/api/investing/fx/${crossId}`);
        if (!priceResponse.ok) {
          throw new Error(`Rates HTTP ${priceResponse.status}`);
        }
        const priceData = await priceResponse.json();
        if (cancelled) return;

        setBars(priceData.bars || []);
      } catch (err) {
        if (!cancelled) {
          setChartError(err.message || 'Failed to load FX history');
          setBars([]);
        }
      } finally {
        if (!cancelled) setChartLoading(false);
      }
    }

    loadChartData();
    return () => {
      cancelled = true;
    };
  }, [selectedRow]);

  useEffect(() => {
    const series = lineSeriesRef.current;
    if (!series) return;

    const lineData = fxBarsToLine(bars);
    series.setData(lineData);
    applyVisibleRange(chartRef.current, bars, zoomRangeRef.current);

    if (selectedRow?.cross_id === 'EURUSD' && lineData.length > 0 && !parityLineRef.current) {
      parityLineRef.current = series.createPriceLine({
        price: 1,
        color: '#787b86',
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: 'parity',
      });
    }
  }, [bars, selectedRow?.cross_id]);

  const handleZoomChange = useCallback(
    (rangeLabel) => {
      zoomRangeRef.current = rangeLabel;
      setZoomRange(rangeLabel);
      applyVisibleRange(chartRef.current, bars, rangeLabel);
    },
    [bars],
  );

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el || !selectedRow) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [selectedRow]);

  const latestBar = bars.length > 0 ? bars[bars.length - 1] : null;
  const displayClose = latestBar?.close ?? selectedRow?.last_close ?? null;
  const displayDate = latestBar?.time ?? selectedRow?.last_date ?? null;

  return (
    <div className="etp-prices-page investing-page page-content fx-page">
      <header className="etp-prices-header">
        <div>
          <h2 className="etp-prices-title">FX crosses</h2>
          <p className="etp-prices-subtitle">
            Spot daily close from Yahoo — quote currency per 1 unit of base (e.g. EURUSD = USD per EUR).
            Chart opens on the last 1Y.
            {meta?.fetched_at && (
              <span className="etp-prices-meta"> FX job {meta.fetched_at}</span>
            )}
          </p>
        </div>
      </header>

      <div className="investing-left etp-left-column">
        <div className="etp-listings-table-block">
          <div className="investing-controls">
            <span className="investing-count">
              {loading ? 'Loading…' : `${sortedRows.length} crosses`}
            </span>
          </div>

          {error && (
            <div className="investing-error">
              Could not load FX catalog: {error}. Is investing backend running on port 5001?
            </div>
          )}

          <div className="investing-table-wrap">
            <table className="investing-table">
              <thead>
                <tr>
                  <th>Cross</th>
                  <th>Pair</th>
                  <th>Convention</th>
                  <th>Yahoo</th>
                  <th>Last</th>
                  <th>As of</th>
                  <th>Bars</th>
                </tr>
              </thead>
              <tbody>
                {!loading && sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="investing-empty">
                      No FX data — migrate V017 and run --fx-only --force-fx
                    </td>
                  </tr>
                )}
                {sortedRows.map((row) => (
                  <tr
                    key={row.cross_id}
                    className={
                      selectedRow?.cross_id === row.cross_id ? 'investing-row-selected' : ''
                    }
                    onClick={() => {
                      zoomRangeRef.current = DEFAULT_ZOOM;
                      setZoomRange(DEFAULT_ZOOM);
                      setSelectedRow(row);
                    }}
                  >
                    <td className="investing-symbol">{row.cross_id}</td>
                    <td className="investing-mono">
                      {row.base_ccy}/{row.quote_ccy}
                    </td>
                    <td>{row.rate_label || '—'}</td>
                    <td className="investing-mono">{row.yahoo_symbol || '—'}</td>
                    <td className="investing-mono">
                      {fmtFxRate(row.last_close, row.quote_ccy)}
                    </td>
                    <td className="investing-mono">{row.last_date || '—'}</td>
                    <td className="investing-mono">{row.count ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="investing-right">
        {!selectedRow && (
          <div className="investing-placeholder">
            <span>Select a cross to view daily rates</span>
          </div>
        )}

        {selectedRow && (
          <div className="etp-right-stack">
            <section className="etp-price-dashboard" aria-label="FX daily rates">
              <div className="investing-chart-header">
                <div className="investing-chart-title">
                  <span className="investing-chart-symbol">{selectedRow.cross_id}</span>
                  <span className="investing-chart-name">
                    {selectedRow.base_ccy}/{selectedRow.quote_ccy}
                  </span>
                  <span className="etp-bucket-badge" title={selectedRow.rate_label}>
                    {selectedRow.rate_label || 'spot'}
                  </span>
                </div>
                <dl className="etp-selected-costs" aria-label="Listing metadata">
                  <div className="etp-selected-cost-item">
                    <dt>Yahoo</dt>
                    <dd>{selectedRow.yahoo_symbol || '—'}</dd>
                  </div>
                  <div className="etp-selected-cost-item">
                    <dt>History</dt>
                    <dd>
                      {selectedRow.first_date && selectedRow.last_date
                        ? `${selectedRow.first_date} → ${selectedRow.last_date}`
                        : bars.length > 0
                          ? `${bars.length} days loaded`
                          : '—'}
                    </dd>
                  </div>
                </dl>
                <div className="investing-chart-meta">
                  {displayClose != null && (
                    <span className="investing-chart-price">
                      {fmtFxRate(displayClose, selectedRow.quote_ccy)}
                      {displayDate && (
                        <span className="investing-chart-date"> · {displayDate}</span>
                      )}
                    </span>
                  )}
                  {chartLoading && <span className="investing-chart-bars">Loading…</span>}
                  {!chartLoading && bars.length > 0 && (
                    <span className="investing-chart-bars">{bars.length} days</span>
                  )}
                </div>
                <div className="investing-zoom-toolbar">
                  <span className="investing-zoom-label">Range</span>
                  {ZOOM_RANGES.map(({ label }) => (
                    <button
                      key={label}
                      type="button"
                      className={`investing-zoom-btn${zoomRange === label ? ' active' : ''}`}
                      onClick={() => handleZoomChange(label)}
                      disabled={chartLoading || bars.length === 0}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {chartError && (
                <div className="investing-error investing-chart-error">
                  Could not load rates for {selectedRow.cross_id}: {chartError}
                </div>
              )}
              {chartLoading && (
                <div className="investing-chart-loading">Loading chart…</div>
              )}
              <div className="investing-charts-area">
                <div
                  ref={chartContainerRef}
                  className="investing-chart-wrap"
                  role="img"
                  aria-label={`Daily FX chart for ${selectedRow.cross_id}`}
                />
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default FxPage;
